import React, { useState, useEffect, useCallback } from 'react';
import { VoiceSettingsPanel } from './VoiceSettingsPanel';
import { useToast, useConfirm } from '../shared/AppDialogs';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export function AdminPage({ onBack }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'voice'

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load users
      let url = `${API_URL}/api/admin/users?limit=100`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (filterPlan) url += `&plan=${filterPlan}`;
      
      const usersRes = await fetch(url, { headers: getAuthHeaders() });
      const usersData = await usersRes.json();
      if (usersData.success) setUsers(usersData.data || []);

      // Load stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats`, { headers: getAuthHeaders() });
      const statsData = await statsRes.json();
      if (statsData.success) setStats(statsData.data);
    } catch (error) {

    }
    setLoading(false);
  }, [search, filterPlan]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpdateUser = async (userId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.success) {
        loadData();
        setEditingUser(null);
      } else {
        toast.error(data.detail || 'Fehler beim Aktualisieren');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (userId, email) => {
    const ok = await confirm({
      title: 'User löschen',
      message: `User "${email}" wirklich löschen? Alle Agenten werden ebenfalls gelöscht!`,
      confirmText: 'Löschen',
      type: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        loadData();
        setSelectedUser(null);
        toast.success('User gelöscht');
      } else {
        toast.error(data.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResetCredits = async (userId) => {
    const ok = await confirm({
      title: 'Credits zurücksetzen',
      message: 'Credits wirklich auf 0 zurücksetzen?',
      confirmText: 'Zurücksetzen',
      type: 'warning',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/reset-credits`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        loadData();
        toast.success('Credits zurückgesetzt');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const planColors = {
    free: 'bg-gray-100 text-gray-700',
    starter: 'bg-blue-100 text-blue-700',
    pro: 'bg-purple-100 text-purple-700',
    business: 'bg-green-100 text-green-700'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Admin Panel</h1>
          <p className="text-gray-500">Benutzer und System verwalten</p>
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          ← Zurück zum Dashboard
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'users'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          👥 Benutzer
        </button>
        <button
          onClick={() => setActiveTab('voice')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'voice'
              ? 'text-emerald-600 border-b-2 border-emerald-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          🎤 Spracheinstellungen
          <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">veraltet</span>
        </button>
      </div>

      {/* Voice Settings Tab */}
      {activeTab === 'voice' && (
        <div className="space-y-4">
          {/* Deprecation Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-semibold text-amber-800">Globale Einstellungen veraltet</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Diese globalen Spracheinstellungen werden nicht mehr verwendet. 
                  Konfigurieren Sie stattdessen die <strong>Experteneinstellungen</strong> direkt 
                  in jedem Agenten unter dem Tab "Verbindungen".
                </p>
                <p className="text-sm text-amber-600 mt-2">
                  ➡️ Agenten → Agent bearbeiten → Verbindungen → Experteneinstellungen
                </p>
              </div>
            </div>
          </div>
          
          {/* Legacy Panel - Read Only Reference */}
          <details className="bg-gray-50 border border-gray-200 rounded-xl">
            <summary className="px-4 py-3 cursor-pointer text-gray-600 hover:bg-gray-100">
              Alte Einstellungen anzeigen (nur Referenz)...
            </summary>
            <div className="p-4 opacity-60">
              <VoiceSettingsPanel />
            </div>
          </details>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <p className="text-3xl font-bold text-blue-600">{stats.users?.total || 0}</p>
                <p className="text-sm text-gray-500">Benutzer gesamt</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <p className="text-3xl font-bold text-green-600">{stats.users?.recent_signups || 0}</p>
                <p className="text-sm text-gray-500">Neue (7 Tage)</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <p className="text-3xl font-bold text-purple-600">{stats.agents?.total || 0}</p>
                <p className="text-sm text-gray-500">Agenten gesamt</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <p className="text-3xl font-bold text-orange-600">{stats.credits?.total_used || 0}</p>
                <p className="text-sm text-gray-500">Credits verbraucht</p>
              </div>
            </div>
          )}

          {/* Plan Distribution */}
          {stats?.users?.by_plan && (
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <h3 className="font-medium text-gray-800 mb-3">Benutzer nach Plan</h3>
          <div className="flex gap-4">
            {Object.entries(stats.users.by_plan).map(([plan, count]) => (
              <div key={plan} className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${planColors[plan] || 'bg-gray-100'}`}>
                  {plan?.toUpperCase()}
                </span>
                <span className="font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border flex gap-4">
        <input
          type="text"
          placeholder="Suche nach Name oder Email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={filterPlan}
          onChange={(e) => setFilterPlan(e.target.value)}
          className="px-4 py-2 border rounded-lg"
        >
          <option value="">Alle Pläne</option>
          <option value="free">Free</option>
          <option value="starter">Starter</option>
          <option value="pro">Pro</option>
          <option value="business">Business</option>
        </select>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          🔄 Aktualisieren
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto"></div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Benutzer</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Plan</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Credits</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Agenten</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-800">{user.name || '-'}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${planColors[user.plan] || 'bg-gray-100'}`}>
                      {user.plan?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <span className={user.credits_remaining <= 10 ? 'text-red-600 font-bold' : 'text-gray-800'}>
                        {user.credits_remaining}
                      </span>
                      <span className="text-gray-400">/{user.credits || 60} Min</span>
                    </div>
                    <div className="w-24 h-1.5 bg-gray-200 rounded-full mt-1">
                      <div 
                        className={`h-full rounded-full ${user.credits_remaining <= 10 ? 'bg-red-500' : 'bg-blue-500'}`}
                        style={{ width: `${Math.max(0, (user.credits_remaining / (user.credits || 60)) * 100)}%` }}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{user.agent_count || 0}</td>
                  <td className="px-4 py-3">
                    {user.is_blocked ? (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">Gesperrt</span>
                    ) : (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">Aktiv</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingUser(user)}
                        className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                      >
                        ✏️ Bearbeiten
                      </button>
                      <button
                        onClick={() => handleResetCredits(user.user_id)}
                        className="px-2 py-1 text-orange-600 hover:bg-orange-50 rounded text-sm"
                      >
                        🔄 Reset
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.user_id, user.email)}
                        className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    Keine Benutzer gefunden
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={(updates) => handleUpdateUser(editingUser.user_id, updates)}
        />
      )}
        </>
      )}
    </div>
  );
}

function EditUserModal({ user, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: user.name || '',
    plan: user.plan || 'free',
    credits: user.credits || 60,
    credits_used: user.credits_used || 0,
    is_blocked: user.is_blocked || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Benutzer bearbeiten</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="text"
              value={user.email}
              disabled
              className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select
              value={formData.plan}
              onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="free">Free (60 Min)</option>
              <option value="starter">Starter (180 Min)</option>
              <option value="pro">Pro (500 Min)</option>
              <option value="business">Business (2000 Min)</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Credits (Gesamt)</label>
              <input
                type="number"
                value={formData.credits}
                onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Verbraucht</label>
              <input
                type="number"
                value={formData.credits_used}
                onChange={(e) => setFormData({ ...formData, credits_used: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_blocked"
              checked={formData.is_blocked}
              onChange={(e) => setFormData({ ...formData, is_blocked: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300"
            />
            <label htmlFor="is_blocked" className="text-sm text-gray-700">
              Benutzer sperren
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminPage;
